import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  Container,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import InfoIcon from '@mui/icons-material/Info';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Content from '../Solicitor/Content';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { sendNotification } from 'src/utils/sendNotification';
import { getBlockLots } from '../common/getBlockLots';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import { getApplicationDetails } from '../common/getApplicationDetails';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';

interface ApplicationDataTypes {
  editApplicationFormData: {
    ApplicantName: string;
    ApplicantAddress: string;
    BlockLot: string;
    LicensedAddress: string;
    OwnerName: string;
    PropAddress: string;
    SquareFeet: string;
    Purpose: string;
    OwnerAddress: string;
  };
}

let editData: any;
const NewApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const socket = useSelector((store: RootState) => store.socket.socket);
  const isManager = currentPermitRole === 'Manager';
  const { townshipId, permit, applicationID } = useParams();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const [loading, setLoading] = useState<boolean>(false);
  const [openApprovePopup, setOpenApprovePopup] = useState<boolean>(false)
  const [blockLotsOptions, setBlockLotsOptions] = useState([]);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [switchTo, setSwitchTo] = useState<string>('');
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState<number>(0);
  const formRef = useRef(null);
  const [applicationDetails, setApplicationDetails] = useState<
    ApplicationDataTypes['editApplicationFormData']
  >({
    ApplicantName: editData?.ApplicantName || '',
    ApplicantAddress: editData?.ApplicantAddress || '',
    BlockLot: editData?.BlockLot || '',
    LicensedAddress: editData?.LicensedAddress || '',
    OwnerName: editData?.OwnerName || '',
    PropAddress: editData?.OwnerAddress || '',
    SquareFeet: editData?.SquareFeet || '',
    Purpose: editData?.Purpose || '',
    OwnerAddress: editData?.OwnerAddress,
  });

  // Get application details
  const handleGetApplicationDetails = async () => {
    const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft
    const response = await getApplicationDetails({ applicationID, permit, isDraft })
    if (response?.status === "success") {
      editData = response?.data?.data[0]
      setApplicationDetails(response?.data?.data[0])
    }
  }

  // Get application details on page load
  useEffect(() => {
    handleGetApplicationDetails()
  }, [applicationID, permit]);

  // Handle dependency
  function handleDependency(val: string, { name }) {
    const data = { ...applicationDetails };
    data[name] = val;
    setApplicationDetails(data);
  }

  // All fields using formik
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.ApplicantName,
      dependency: handleDependency
    },
    {
      fieldName: 'ApplicantAddress',
      fieldLabel: 'Applicant Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.ApplicantAddress,
      dependency: handleDependency
    },
    {
      fieldName: 'BlockLot',
      fieldLabel: 'Block Lot',
      options: blockLotsOptions,
      fieldType: 'autocomplete',
      required: true,
      initialValue: applicationDetails.BlockLot || '',
      dependency: async (val: any) => {
        if (val?.length > 2) {
          const blockLotsData = await getBlockLots(val);
          setBlockLotsOptions(blockLotsData);
        } else if (typeof val === 'object') {
          const data = { ...applicationDetails };
          data.OwnerName = val.OwnerName;
          data.BlockLot = val.BlockLot;
          data.LicensedAddress = val.LicensedAddress;
          data.PropAddress = val.PropAddress;
          setApplicationDetails(data);
        }
      }
    },
    {
      fieldName: 'LicensedAddress',
      fieldLabel: 'License Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.LicensedAddress || ''
    },
    {
      fieldName: 'OwnerName',
      fieldLabel: 'Owner Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationDetails.OwnerName || ''
    },
    {
      fieldName: 'OwnerAddress',
      fieldLabel: 'Owner Address ',
      fieldType: 'note',
      required: true,
      initialValue: applicationDetails.PropAddress || applicationDetails?.OwnerAddress
    },
    {
      fieldName: 'SquareFeet',
      fieldLabel: 'Square ft ',
      fieldType: 'number',
      required: true,
      initialValue: applicationDetails.SquareFeet || '',
      dependency: handleDependency
    },
    {
      fieldName: 'Purpose',
      fieldLabel: 'Purpose',
      fieldType: 'note',
      initialValue: applicationDetails.Purpose || '',
      dependency: handleDependency
    }
  ];

  const handleProceedToPay = async (data: any) => {
    const response = await httpRequest(
      'PATCH',
      `/ucd_application/update/${editData.ApplicationID}`,
      data
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };

      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: editData?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix: 'UsedCar'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Method to update application
  const handleUpdateApplication = async (data: any, type = '') => {
    setLoading(true);
    const response = await httpRequest(
      'PATCH',
      `/ucd_application/update/${editData.ApplicationID}`,
      data
    );
    if (response?.status === 'success') {
      setLoading(false);
      if (type) return true;
      toast.success(`Application update successfully.`);
      if (editData.ApplicationStatus === 'Draft')
        navigate(`/${townshipId}/Clerk Department/${permit}/draft-application`);
      else
        navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Method to approve application
  const handleApproveApplication = async () => {
    setOpenApprovePopup(false)
    setLoading(true);
    function getPermitExpiration(): string {
      const issue = new Date();
      const exp = new Date(issue.getFullYear() + 1, 0, 1);
      const month = (exp.getMonth() + 1).toString().padStart(2, '0');
      const day = exp.getDate().toString().padStart(2, '0');
      const year = exp.getFullYear();
      return `${month}/${day}/${year}`;
    }

    const payload = {
      ApplicationID: Number(editData.ApplicationID),
      ApplicationStatus: 'Approved',
      Address: applicationDetails.OwnerAddress,
      Purpose: applicationDetails.Purpose,
      BlockLot: applicationDetails.BlockLot,
      LicensedAddress: applicationDetails.LicensedAddress,
      PermitExpiration: getPermitExpiration()
    };
    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );
    if (response?.status === 'success') {
      sendNotification(socket, {
        UserID: editData?.UserID, // receiver user ID --
        TownshipID: Number(townshipId),
        ApplicationID: editData?.ApplicationID,
        PermitID: Number(permit),
        Title: 'Application Approved',
        Message: `Used Car Dealer application with the no. (${editData?.ApplicationNumber}) is approved successfully.`,
        ApplicationNumber: editData?.ApplicationNumber
      });
      toast.success('Application approved successfully.');
      navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };

  // Method to handle form submit
  const handleSubmit = async (data) => {
    if (clickedOn === 'Proceed to Payment') {
      handleProceedToPay(data);
    } else if (clickedOn === 'Update') {
      handleUpdateApplication(data);
    } else if (clickedOn === 'Approve') {
      const applicationUpdated = await handleUpdateApplication(data, 'Approve');
      if (applicationUpdated) setOpenApprovePopup(true);
    }
  };

  // Method to get application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: Number(feeObject?.ApplicationFee),
        inspectionFee: Number(feeObject?.InspectionFee)
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Get application fee on page load
  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
    return () => {
      editData = '';
    };
  }, []);

  // Method to get form buttons
  const getFormButtons = () => {
    let buttonsCollection = ['Cancel'];
    if (editData?.ApplicationStatus) {
      if (
        editData?.ApplicationStatus === 'In Review' ||
        editData?.ApplicationStatus === 'Draft'
      ) {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }
      if (editData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Proceed to Payment'];
      }
      if (isManager && editData?.ApplicationStatus === 'In Review') {
        buttonsCollection = [...buttonsCollection, 'Approve'];
      }
    }
    return buttonsCollection;
  };
  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb
        PermitName={'Clerk Used Car Dealer'}
        PageTitle={'Edit Application'}
      />

      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={'edit'}
        applicationStatus={editData?.ApplicationStatus}
        applicationNumber={editData?.ApplicationNumber}
      />

      {switchTo ? (
        <Content
          switchTo={switchTo}
          setSwitchTo={setSwitchTo}
          applicationID={editData?.ApplicationID}
          UserID={userData?.UserID}
          ApplicationStatus={editData?.ApplicationStatus}
          ApplicationNumber={editData?.ApplicationNumber}
          selectMultipleFiles={true}
        />
      ) : (
        <Card>
          <Box
            m={{xs: 1.5, sm:3}}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {editData && editData?.ApplicationStatus !== 'Draft' ? (
              <>
                <Typography
                  color={
                    editData?.ApplicationStatus === 'Approved'
                      ? 'green'
                      : '#8B0000'
                  }
                  fontSize={{xs : "14px", sm:15}}
                  fontWeight={500}
                >
                  {editData?.ApplicationStatus === 'Approved'
                    ? `This application has been successfully approved ${editData?.ApprovedDate &&
                    ` on ${format(
                      new Date(editData?.ApprovedDate),
                      'MM/dd/yyyy'
                    )}`
                    }.`
                    : editData?.ApplicationStatus === 'Rejected'
                      ? `This application has been rejected${editData?.RejectedDate &&
                      ` on ${format(
                        new Date(editData?.RejectedDate),
                        'MM/dd/yyyy'
                      )}`
                      }.`
                      : ''}
                  {editData?.ApplicationStatus === 'Rejected' && (
                    <Tooltip
                      title={
                        'Reason of rejection -' +
                        ' ' +
                        editData?.ReasonOfRejection
                      }
                      arrow
                    >
                      <IconButton>
                        <InfoIcon color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Typography>
              </>
            ) : (
              <Typography variant="h4">
                {editData ? 'Edit' : 'New'} Application
              </Typography>
            )}

            <PdfGenerator
              PermitName={'Clerk Used Car Dealer'}
              allFields={allFields}
            />
          </Box>

          <Box m={{ md: 3, xs: 1 }}>
            <FormikForm
              allFields={allFields}
              innerRef={formRef}
              buttonPlacement={'right'}
              defaultEnabledButton={['Proceed to Payment', 'Approve', 'Update']}
              buttonTitle={
                editData
                  ? getFormButtons()
                  : ['Cancel', 'Save Draft', 'Proceed to Payment']
              }
              setClickedOn={setClickedOn}
              clickProp
              content={
                <>
                  <Box display={'flex'} gap={2}>
                    <TextField
                      id="outlined-basic"
                      disabled
                      sx={{ mt: 2 }}
                      value={`$ ${feeDetails?.applicationFee}.00`}
                      label="Application Fee"
                      variant="outlined"
                      InputLabelProps={{
                        className: 'weight-600'
                      }}
                    />
                  </Box>
                  {editData && (
                    <Box sx={{ display: 'flex' }}>
                      <Content
                        switchTo={switchTo}
                        setSwitchTo={setSwitchTo}
                        applicationID={editData?.ApplicationID}
                        UserID={userData?.UserID}
                        ApplicationStatus={editData?.ApplicationStatus}
                        ApplicationNumber={editData?.ApplicationNumber}
                        selectMultipleFiles={false}
                      />
                    </Box>
                  )}
                </>
              }
              formMethod={handleSubmit}
              heading={'Clerk Department'}
              handleOtherButton={() => {
                navigate(`/dashboard/${permit}`);
              }}
            />
          </Box>
        </Card>
      )}
      <CustomModal
        title={'Approve Application?'}
        IsOpen={openApprovePopup}
        setIsOpen={setOpenApprovePopup}
        onSave={handleApproveApplication}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>Are you sure you want to approve this application?</Typography>
        }
      />
    </Container>
  );
};

export default NewApplication;
